/**
 * Corrige la sélection AVANT que le thème n'aille chercher la variante.
 *
 * Sur le t-shirt SWAG, deux combinaisons n'existent pas : un print blanc sur un
 * t-shirt blanc, un print noir sur un t-shirt noir. Le CSS de la marque masque
 * le choix impossible au lieu de le barrer, ce qui est plus clair — mais crée
 * un piège : si le client a choisi le print blanc puis bascule sur le t-shirt
 * blanc, son choix reste coché alors qu'il vient de disparaître de l'écran.
 *
 * La première version rattrapait le coup après coup, en cliquant une couleur
 * valide une fois la page revenue. Deux requêtes partaient alors coup sur coup
 * et la galerie ne s'en remettait pas : elle perdait son carrousel, se dépliait
 * en grille et laissait la photo précédente en tête — on cliquait « blanc » et
 * on continuait de voir le t-shirt noir.
 *
 * On corrige donc en amont. Le sélecteur de Horizon écoute `change` sur
 * lui-même, en phase de bulle, puis construit sa requête à partir de TOUS les
 * boutons cochés (`selectedOptionsValues`). Un écouteur en phase de capture
 * passe avant lui : on y coche la bonne couleur de design sans émettre
 * d'événement, si bien que le thème ne voit qu'un seul changement, cohérent,
 * et ne lance qu'une seule requête.
 */

const OPTION_DESIGN = 'Couleur du design';
const OPTION_TSHIRT = 'Couleur du t-shirt';

/* La couleur de contraste maximal, donc la plus proche de ce que le client
   venait de choisir quand son choix devient impossible. */
const INVERSE = { Noir: 'Blanc', Blanc: 'Noir' };

const boutons = (racine, nom) => [...racine.querySelectorAll(`input[data-option-name="${nom}"]`)];

const champVariante = () => document.querySelector('input[name="id"]');

/**
 * Remet `?variant=` dans la barre d'adresse.
 *
 * Le thème renseigne ce paramètre depuis le bouton cliqué, or celui-ci porte
 * l'identifiant de la combinaison qu'on vient justement d'écarter : elle
 * n'existe pas, l'identifiant est vide, et le paramètre saute. On le repose
 * une fois la section revenue, à partir de la variante que le serveur a
 * réellement retenue — sans quoi un rechargement ou un lien partagé
 * retomberait sur la variante par défaut.
 *
 * On attend en lisant, jamais en cliquant : c'est ce qui distingue ce rattrapage
 * de la version précédente, et ce qui évite de relancer une requête.
 *
 * L'attente se compte en temps, pas en images. Une boucle sur
 * `requestAnimationFrame` paraissait naturelle ici, mais elle ne s'exécute que
 * si la page peint : dans un onglet passé à l'arrière-plan pendant le
 * chargement, elle s'arrête et l'adresse ne se met jamais à jour. Un minuteur
 * tourne dans tous les cas.
 */
const ATTENTE_MAX = 8000;
const PAS_ATTENTE = 120;

function reposerVarianteDansUrl() {
  const depart = champVariante()?.value;
  const limite = Date.now() + ATTENTE_MAX;

  const tic = () => {
    const actuel = champVariante()?.value;
    if (actuel && actuel !== depart) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== actuel) {
        url.searchParams.set('variant', actuel);
        history.replaceState({}, '', url.toString());
      }
      return;
    }
    if (Date.now() < limite) setTimeout(tic, PAS_ATTENTE);
  };

  setTimeout(tic, PAS_ATTENTE);
}

/**
 * Un design de la même couleur que le vêtement ne se voit pas : c'est la règle
 * du produit, et elle vaut avant même que le serveur ait renvoyé la page. On ne
 * peut pas s'appuyer sur `data-option-available`, qui décrit encore l'état
 * précédent au moment où l'on intervient.
 *
 * La règle se lit dans les deux sens, et on la corrige dans les deux sens : le
 * choix que le client vient de faire est toujours celui qu'on garde, c'est
 * l'autre qui cède. Le CSS masque déjà le choix impossible, mais s'appuyer sur
 * ce masquage seul laisserait l'impasse revenir au premier oubli.
 */
function corrigerAvantRequete(event) {
  const cible = event.target;
  if (!(cible instanceof HTMLInputElement) || !cible.checked) return;

  const nom = cible.dataset.optionName;
  if (nom !== OPTION_TSHIRT && nom !== OPTION_DESIGN) return;
  const oppose = nom === OPTION_TSHIRT ? OPTION_DESIGN : OPTION_TSHIRT;

  const picker = cible.closest('variant-picker, variant-picker-component') ?? cible.form ?? document;
  const voisins = boutons(picker, oppose);
  if (voisins.length === 0) return;

  const coche = voisins.find((input) => input.checked);
  if (!coche || coche.value !== cible.value) return;

  const repli =
    voisins.find((input) => input.value === INVERSE[cible.value]) ??
    voisins.find((input) => input.value !== cible.value);
  if (!repli) return;

  // Affectation directe : cocher une radio décoche les autres du même groupe,
  // et n'émet aucun événement — le thème ne partira donc pas une seconde fois.
  repli.checked = true;
  reposerVarianteDansUrl();
}

document.addEventListener('change', corrigerAvantRequete, true);
